import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { LockKeyhole, ShieldCheck, ArrowLeft, CheckCircle2, AlertCircle } from "lucide-react";
import { resetPassword } from "@/api/auth";

export default function ResetPassword() {
  const navigate = useNavigate();
  const params = useParams();

  const searchParams = new URLSearchParams(window.location.search);
  const token =
    searchParams.get("token") ||
    params.token ||
    window.location.pathname.split("/").filter(Boolean).pop();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [ok, setOk] = useState("");
  const [err, setErr] = useState("");

  const onSubmit = async (event) => {
    event.preventDefault();
    setErr("");
    setOk("");

    if (!token || token === "reset-password") {
      setErr("Lien de réinitialisation invalide ou incomplet.");
      return;
    }

    if (password.length < 8) {
      setErr("Le mot de passe doit contenir au moins 8 caractères.");
      return;
    }

    if (password !== confirm) {
      setErr("Les deux mots de passe ne correspondent pas.");
      return;
    }

    try {
      setLoading(true);
      await resetPassword({ token, password, new_password: password });
      setOk("Mot de passe modifié avec succès. Redirection vers la connexion...");
      setTimeout(() => navigate("/login"), 1200);
    } catch (error) {
      setErr(
        error?.response?.data?.detail ||
        "Impossible de modifier le mot de passe. Le lien est peut-être expiré."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen aurora-mesh flex items-center justify-center px-6 py-10 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-28 -left-28 w-80 h-80 rounded-full bg-[var(--do-accent-soft)] blur-3xl" />
        <div className="absolute bottom-[-120px] right-[-80px] w-96 h-96 rounded-full bg-[var(--do-accent-soft)] blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-sm font-medium text-[var(--do-muted)] hover:text-[var(--do-accent)] mb-5"
        >
          <ArrowLeft size={16} />
          Retour à la connexion
        </Link>

        <div className="rounded-[28px] border border-[var(--do-border)] bg-[var(--do-bg-elev)] backdrop-blur-xl shadow-2xl shadow-[var(--do-shadow-blue)] p-7 sm:p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 rounded-2xl bg-[var(--do-accent-strong)] text-[var(--do-on-accent)] flex items-center justify-center shadow-lg shadow-[var(--do-shadow-blue)]">
              <LockKeyhole size={22} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-[var(--do-accent)] font-bold">
                AlmaCRM
              </p>
              <h1 className="text-2xl font-bold text-[var(--do-ink)]">
                Nouveau mot de passe
              </h1>
            </div>
          </div>

          <p className="text-sm text-[var(--do-muted)] mb-6 leading-6">
            Choisissez un nouveau mot de passe sécurisé pour récupérer l’accès à votre espace.
          </p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-[var(--do-ink)] mb-2">
                Nouveau mot de passe
              </label>
              <input
                type="password"
                autoComplete="new-password"
                minLength={8}
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Minimum 8 caractères"
                className="w-full rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-[var(--do-ink)] outline-none transition focus:border-[var(--do-border-strong)] focus:ring-4 focus:ring-[var(--do-accent)]"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-[var(--do-ink)] mb-2">
                Confirmer le mot de passe
              </label>
              <input
                type="password"
                autoComplete="new-password"
                minLength={8}
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Répétez le mot de passe"
                className="w-full rounded-2xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-4 py-3 text-[var(--do-ink)] outline-none transition focus:border-[var(--do-border-strong)] focus:ring-4 focus:ring-[var(--do-accent)]"
              />
            </div>

            {err && (
              <div className="flex gap-2 rounded-2xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 py-3 text-sm text-[var(--do-accent)]">
                <AlertCircle size={18} className="shrink-0 mt-0.5" />
                <span>{err}</span>
              </div>
            )}

            {ok && (
              <div className="flex gap-2 rounded-2xl border border-[var(--do-border)] bg-[var(--do-accent-soft)] px-4 py-3 text-sm text-[var(--do-accent)]">
                <CheckCircle2 size={18} className="shrink-0 mt-0.5" />
                <span>{ok}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-2xl bg-[var(--do-accent-strong)] px-5 py-3.5 text-[var(--do-on-accent)] font-bold shadow-lg shadow-[var(--do-shadow-blue)] hover:bg-[var(--do-accent-strong)] disabled:opacity-60 disabled:cursor-not-allowed transition"
            >
              {loading ? "Modification en cours..." : "Modifier mon mot de passe"}
            </button>
          </form>

          <div className="mt-6 flex items-start gap-3 rounded-2xl bg-[var(--do-bg-solid)] border border-[var(--do-border)] px-4 py-3">
            <ShieldCheck size={18} className="text-[var(--do-accent)] shrink-0 mt-0.5" />
            <p className="text-xs text-[var(--do-muted)] leading-5">
              Pour votre sécurité, le lien de réinitialisation est temporaire et ne peut être utilisé qu’une seule fois.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
